import React, { useRef, useState } from 'react';
import ImageCropModal from './ImageCropModal';
import {
  ACCEPTED_IMAGE_TYPES,
  createImageId,
  isSupportedImageType,
  loadImageDetails,
  readFileAsDataUrl,
} from '../lib/imageUtils';

function ImageUploader({ images, onChange }) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [readingCount, setReadingCount] = useState(0);
  const [cropTarget, setCropTarget] = useState(null);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;

    const supportedFiles = files.filter(isSupportedImageType);
    const unsupportedFiles = files.filter((file) => !isSupportedImageType(file));

    if (unsupportedFiles.length > 0) {
      setErrorMessage(
        `Unsupported image format: ${unsupportedFiles.map((file) => file.name).join(', ')}. Use JPG, JPEG, PNG, WEBP, GIF, or SVG.`
      );
    } else {
      setErrorMessage('');
    }

    if (supportedFiles.length === 0) {
      return;
    }

    setReadingCount((count) => count + supportedFiles.length);

    try {
      const selectedImages = await Promise.all(
        supportedFiles.map(async (file) => {
          const src = await readFileAsDataUrl(file);
          const dimensions = await loadImageDetails(src);

          return {
            id: createImageId(),
            name: file.name,
            src,
            alt: '',
            type: file.type,
            width: dimensions.width,
            height: dimensions.height,
            previewSrc: null,
            driveFileId: null,
            storageProvider: null,
          };
        })
      );

      onChange([...images, ...selectedImages]);
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || 'Failed to load one or more images.');
    } finally {
      setReadingCount((count) => Math.max(0, count - supportedFiles.length));
    }
  };

  const openPicker = () => {
    inputRef.current?.click();
  };

  return (
    <div className="form-group">
      <label className="form-label" htmlFor="entry-images-input">
        Images
      </label>

      <input
        ref={inputRef}
        id="entry-images-input"
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        multiple
        className="sr-only"
        onChange={async (event) => {
          await handleFiles(event.target.files);
          event.target.value = '';
        }}
        aria-label="Upload images"
      />

      <div
        className={`upload-zone ${dragActive ? 'is-drag-active' : ''}`}
        role="button"
        tabIndex={0}
        aria-label="Drag and drop images here or click to upload"
        onClick={openPicker}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPicker();
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragActive(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          if (event.target === event.currentTarget) {
            setDragActive(false);
          }
        }}
        onDrop={async (event) => {
          event.preventDefault();
          setDragActive(false);
          await handleFiles(event.dataTransfer.files);
        }}
      >
        <div className="upload-zone-icon" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            width="30"
            height="30"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </div>
        <strong>Drag & Drop or Click to Upload</strong>
        <span>JPG, JPEG, PNG, WEBP, GIF, SVG</span>
        <span>Images upload to your Google Drive when you save this entry.</span>
      </div>

      {errorMessage && (
        <div className="upload-error" role="alert">
          {errorMessage}
        </div>
      )}

      {(images.length > 0 || readingCount > 0) && (
        <div className="upload-preview-grid">
          {images.map((image) => (
            <div key={image.id} className="upload-preview-card">
              <img
                src={image.previewSrc || image.src}
                alt={image.alt || image.name || 'Uploaded preview'}
                className="upload-preview-image"
              />
              <div className="upload-preview-meta">
                <span className="upload-preview-name">{image.name}</span>
                <div className="upload-preview-actions">
                  <button
                    type="button"
                    className="preview-action"
                    onClick={() => setCropTarget(image)}
                  >
                    Crop
                  </button>
                  <button
                    type="button"
                    className="preview-action preview-action-danger"
                    onClick={() => onChange(images.filter((item) => item.id !== image.id))}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <label className="preview-alt-label">
                Alt text
                <input
                  type="text"
                  className="form-control"
                  value={image.alt}
                  onChange={(event) => {
                    onChange(
                      images.map((item) =>
                        item.id === image.id
                          ? { ...item, alt: event.target.value }
                          : item
                      )
                    );
                  }}
                  placeholder="Optional description for accessibility"
                />
              </label>
            </div>
          ))}

          {Array.from({ length: readingCount }).map((_, index) => (
            <div
              key={`reading-${index}`}
              className="upload-preview-card is-loading"
              aria-hidden="true"
            >
              <div className="upload-preview-image upload-preview-skeleton" />
              <div className="upload-preview-skeleton-line" />
              <div className="upload-preview-skeleton-line short" />
            </div>
          ))}
        </div>
      )}

      {cropTarget && (
        <ImageCropModal
          image={cropTarget}
          onClose={() => setCropTarget(null)}
          onConfirm={(croppedImage) => {
            onChange(
              images.map((image) => (image.id === croppedImage.id ? croppedImage : image))
            );
            setCropTarget(null);
          }}
        />
      )}
    </div>
  );
}

export default ImageUploader;
